import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Entity, Enemy, LootChest } from '../labyrinth/Entity';
import { createEnemyRig, createLootRig, type EnemyRig, type LootRig } from './EnemyRigs';
import type { LightmapBindings } from './StylizedShading';

type RigEntry =
  | { kind: 'enemy'; rig: EnemyRig }
  | { kind: 'loot';  rig: LootRig };

/**
 * 3D primitive-based enemy + loot renderer — replacement for the baked-billboard
 * path in EntityBillboards. Each entity gets its own Group rig (see EnemyRigs.ts);
 * this manager handles lifecycle (create on first sight, update every frame,
 * dispose on removal) and world-space positioning.
 *
 * The player robot is NOT managed here — see RobotRig.ts / LabyrinthState.
 */
export class EntityMeshes {
  readonly group = new THREE.Group();
  private readonly perEntity = new Map<Entity, RigEntry>();

  constructor(private readonly lightmap: LightmapBindings) {}

  syncEntities(entities: Entity[], t: number): void {
    const alive = new Set<Entity>();
    for (const entity of entities) {
      if (!entity.alive || entity.visAlpha <= 0) continue;
      alive.add(entity);

      let entry = this.perEntity.get(entity);
      if (!entry) {
        entry = this.createEntry(entity);
        this.perEntity.set(entity, entry);
        this.group.add(entry.rig.root);
      }

      if (entry.kind === 'enemy') entry.rig.update(entity as Enemy, t);
      else                        entry.rig.update(entity as LootChest, t);

      entry.rig.root.position.x = entity.x * TILE_SIZE;
      entry.rig.root.position.z = entity.y * TILE_SIZE;
    }

    // Remove rigs for entities that are no longer alive/visible.
    for (const [entity, entry] of this.perEntity) {
      if (alive.has(entity)) continue;
      this.group.remove(entry.rig.root);
      entry.rig.dispose();
      this.perEntity.delete(entity);
    }
  }

  private createEntry(entity: Entity): RigEntry {
    if (entity.type === 'enemy') {
      return { kind: 'enemy', rig: createEnemyRig(entity as Enemy, this.lightmap) };
    }
    return { kind: 'loot', rig: createLootRig(this.lightmap) };
  }

  dispose(): void {
    for (const entry of this.perEntity.values()) {
      this.group.remove(entry.rig.root);
      entry.rig.dispose();
    }
    this.perEntity.clear();
  }
}
